import { t } from '@transifex/native';

import { flashes } from 'pkg/actions';
import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { create as modelsCreate } from 'pkg/api/models/actions';
import * as sdk from 'pkg/core/sdk';

export interface CreateOpponentPayload {
	groupID: number;
	name: string;
	profileImageAttachmentId?: number;
}

export interface UpdateOpponentPayload {
	name: string;
	profileImageAttachmentId?: number;
}

export const create = async (
	organizationID: number,
	payload: CreateOpponentPayload
): Promise<[Response, models.opponent.Opponent]> => {
	const [request, response] = await modelsCreate<
		CreateOpponentPayload,
		models.opponent.Opponent
	>(endpoints.Opponents.Create(organizationID), payload);

	if (!request.ok) {
		flashes.show({
			title: t('Something went wrong!'),
			message: t(
				`If you think this error isn't supposed to happen, please contact 360Player support.`
			),
		});
		return [request, null];
	}

	return [request, response];
};

export const update = async (
	organizationID: number,
	opponentID: number,
	payload: UpdateOpponentPayload
): Promise<[Response, models.opponent.Opponent]> => {
	const request = await sdk.patch(
		endpoints.Opponents.Update(organizationID, opponentID),
		{},
		payload
	);

	if (!request.ok) {
		flashes.show({
			title: t('Something went wrong!'),
			message: t(
				`If you think this error isn't supposed to happen, please contact 360Player support.`
			),
		});
		return [request, null];
	}

	const response = await request.json();

	return [request, response];
};

export const merge = async (
	organizationID: number,
	sourceOpponentID: number,
	targetOpponentID: number
): Promise<[Response, models.opponent.Opponent]> => {
	const request = await sdk.post(
		endpoints.Opponents.Merge(organizationID),
		{},
		{
			sourceOpponentId: sourceOpponentID,
			targetOpponentId: targetOpponentID,
		}
	);

	const response = await request.json();

	if (!request.ok) {
		flashes.show(
			{
				title: t('Something went wrong!'),
				message: response.error,
			},
			request.status
		);
		return [request, null];
	}

	flashes.show({
		title: t('Successfully merged opponents!'),
	});

	return [request, response];
};

export const remove = async (
	organizationID: number,
	opponentID: number
): Promise<[Response, models.opponent.Opponent]> => {
	const request = await sdk.destroy(
		endpoints.Opponents.Delete(organizationID, opponentID),
		{}
	);

	const response = await request.json();

	if (!request.ok) {
		flashes.show(
			{
				title: t('Something went wrong!'),
				message: response.error,
			},
			request.status
		);
		return [request, null];
	}

	return [request, response];
};
